/**
 * Qualified capacity (Planning Resource Model PRD, Part A Phase B / Phase C).
 *
 * The judgment question the whole PRD exists to answer: when the engine says
 * "below the line — no engineering capacity", can it defend that answer at the
 * resolution a real staffing conversation happens at — which people, in which
 * roles, with what skills, at what level, holding which certifications, and
 * whose hours can actually move to cover the gap?
 *
 * The greedy allocation loop is unchanged in order and in spirit. Only the pool
 * lookup narrows: instead of one number per resource type, supply is a set of
 * units (one per role assignment, plus one per manual pool row), and an item
 * draws only from the units that satisfy its requirements. Consumption
 * decrements the unit, and therefore both the qualified slice and the parent
 * pool — so an item further down the rank with no requirements still sees the
 * correct remainder.
 *
 * Pure: no React, no Supabase.
 */

import type {
  PlanningRequirement,
  QualificationInput,
  QualifiedSupplyUnit,
} from './planningEngineModule.types';

// ---------------------------------------------------------------------------
// §1 — Does one unit satisfy one requirement?
// ---------------------------------------------------------------------------

/** Why a supply unit failed a requirement — the vocabulary of the reason string. */
export type DisqualificationCode =
  | 'lacks_capability'
  | 'below_level'
  | 'lacks_credential'
  | 'credential_expires'
  | 'not_the_named_resource'
  | 'unattributable';

export interface UnitVerdict {
  qualified: boolean;
  code: DisqualificationCode | null;
}

/**
 * A credential is a hard gate, not a proficiency.
 *
 * v1 simplification, deliberately conservative: an assignment whose required
 * credential expires inside the horizon counts as unqualified for the whole
 * horizon, and says so. Slicing supply at the expiry date is future work; it is
 * better to be visibly strict than quietly optimistic about compliance.
 */
export function credentialCoversHorizon(
  expiresAt: string | null,
  horizonEndDate: string | null,
): boolean {
  if (!expiresAt) return true;
  if (!horizonEndDate) return Date.parse(expiresAt) >= Date.now();
  return Date.parse(expiresAt) >= Date.parse(horizonEndDate);
}

export function unitSatisfies(
  unit: QualifiedSupplyUnit,
  requirement: PlanningRequirement,
  horizonEndDate: string | null,
): UnitVerdict {
  // Hours nobody stands behind cannot answer a question about who will do the
  // work. They still fund requirement-free demand — see qualifiedRemaining.
  if (!unit.attributable) return { qualified: false, code: 'unattributable' };

  switch (requirement.dimension) {
    case 'capability': {
      const capabilityId = requirement.capabilityId;
      if (!capabilityId) return { qualified: true, code: null };
      const held = unit.capabilityLevels[capabilityId];
      if (held == null) return { qualified: false, code: 'lacks_capability' };
      if (requirement.minLevel != null && held < requirement.minLevel) {
        return { qualified: false, code: 'below_level' };
      }
      return { qualified: true, code: null };
    }
    case 'credential': {
      const credentialId = requirement.credentialId;
      if (!credentialId) return { qualified: true, code: null };
      if (!(credentialId in unit.credentials)) {
        return { qualified: false, code: 'lacks_credential' };
      }
      if (!credentialCoversHorizon(unit.credentials[credentialId], horizonEndDate)) {
        return { qualified: false, code: 'credential_expires' };
      }
      return { qualified: true, code: null };
    }
    case 'named_resource': {
      if (!requirement.namedResourceId) return { qualified: true, code: null };
      return unit.resourceId === requirement.namedResourceId
        ? { qualified: true, code: null }
        : { qualified: false, code: 'not_the_named_resource' };
    }
  }
}

// ---------------------------------------------------------------------------
// §2 — The ledger
// ---------------------------------------------------------------------------

interface UnitState {
  unit: QualifiedSupplyUnit;
  consumed: number;
}

/** What the ledger could not fund, and why — one requirement, named. */
export interface QualificationShortfall {
  requirement: PlanningRequirement;
  code: DisqualificationCode;
  /** Attributable units of this type that failed this requirement this way. */
  failingUnitCount: number;
  /** Work units the qualified slice could have funded. */
  qualifiedRemainingWU: number;
}

export interface AllocationVerdict {
  /** Room in the qualified slice for the whole ask. */
  fits: boolean;
  /** Room somewhere in the parent pool, even if not in the qualified slice. */
  poolHasRoom: boolean;
  qualifiedRemainingWU: number;
  poolRemainingWU: number;
  /** Populated when the qualified slice is short but the pool is not. */
  shortfall: QualificationShortfall | null;
}

/**
 * Per-unit capacity accounting for a run.
 *
 * Only constructed when a run carries qualification input. Without it the
 * engine keeps its original per-resource-type map arithmetic untouched, which
 * is what makes the backward-compatibility invariant hold by construction
 * rather than by testing.
 */
export type AllocationGrain = 'work_unit' | 'priority';

export class QualifiedCapacityLedger {
  private readonly byType = new Map<string, UnitState[]>();
  private readonly requirementsByPriority = new Map<string, PlanningRequirement[]>();
  private readonly requirementsByWorkUnit = new Map<string, PlanningRequirement[]>();
  private readonly horizonEndDate: string | null;

  constructor(input: QualificationInput) {
    this.horizonEndDate = input.horizonEndDate;
    for (const unit of input.supply) {
      const list = this.byType.get(unit.resourceType) ?? [];
      list.push({ unit, consumed: 0 });
      this.byType.set(unit.resourceType, list);
    }
    for (const requirement of input.requirements) {
      if (requirement.priorityId) {
        const list = this.requirementsByPriority.get(requirement.priorityId) ?? [];
        list.push(requirement);
        this.requirementsByPriority.set(requirement.priorityId, list);
      } else if (requirement.workUnitId) {
        const list = this.requirementsByWorkUnit.get(requirement.workUnitId) ?? [];
        list.push(requirement);
        this.requirementsByWorkUnit.set(requirement.workUnitId, list);
      }
    }
  }

  requirementsForPriority(priorityId: string): PlanningRequirement[] {
    return this.requirementsByPriority.get(priorityId) ?? [];
  }

  requirementsForWorkUnit(workUnitId: string, priorityId: string | null): PlanningRequirement[] {
    // A priority-level requirement applies to every work unit under it — the
    // same rule participation rows already use for workUnitId === null.
    const own = this.requirementsByWorkUnit.get(workUnitId) ?? [];
    const inherited = priorityId ? this.requirementsForPriority(priorityId) : [];
    return [...own, ...inherited];
  }

  hasModel(resourceType: string): boolean {
    return (this.byType.get(resourceType)?.length ?? 0) > 0;
  }

  /** Every resource type the roster or the manual rows put supply behind. */
  resourceTypes(): string[] {
    return [...this.byType.keys()];
  }

  private states(resourceType: string, grain: AllocationGrain): UnitState[] {
    return (this.byType.get(resourceType) ?? []).filter(
      (s) => s.unit.grain === 'any' || s.unit.grain === grain,
    );
  }

  private remainingOf(state: UnitState): number {
    return Math.max(0, state.unit.workUnitCapacity - state.consumed);
  }

  /**
   * Units that may fund this ask.
   *
   * With no requirements, that is every unit of the type — including manual
   * rows, which is what keeps a scenario that mixes roster and manual capacity
   * behaving sensibly. Dedicated (reserved) hours only ever fund their own
   * priority, in either direction.
   */
  private eligible(
    resourceType: string,
    requirements: PlanningRequirement[],
    priorityId: string | null,
    grain: AllocationGrain,
  ): UnitState[] {
    return this.states(resourceType, grain).filter((state) => {
      const dedicated = state.unit.dedicatedToPriorityId;
      if (dedicated && dedicated !== priorityId) return false;
      return requirements.every(
        (r) => unitSatisfies(state.unit, r, this.horizonEndDate).qualified,
      );
    });
  }

  poolRemaining(resourceType: string, priorityId: string | null, grain: AllocationGrain): number {
    return this.states(resourceType, grain)
      .filter((s) => !s.unit.dedicatedToPriorityId || s.unit.dedicatedToPriorityId === priorityId)
      .reduce((sum, s) => sum + this.remainingOf(s), 0);
  }

  evaluate(args: {
    resourceType: string;
    requiredWU: number;
    requirements: PlanningRequirement[];
    priorityId: string | null;
    grain: AllocationGrain;
  }): AllocationVerdict {
    const { resourceType, requiredWU, requirements, priorityId, grain } = args;
    const eligible = this.eligible(resourceType, requirements, priorityId, grain);
    const qualifiedRemainingWU = eligible.reduce((sum, s) => sum + this.remainingOf(s), 0);
    const poolRemainingWU = this.poolRemaining(resourceType, priorityId, grain);
    const fits = qualifiedRemainingWU >= requiredWU;

    return {
      fits,
      poolHasRoom: poolRemainingWU >= requiredWU,
      qualifiedRemainingWU,
      poolRemainingWU,
      shortfall:
        fits || requirements.length === 0
          ? null
          : this.diagnose(resourceType, requirements, priorityId, grain, qualifiedRemainingWU),
    };
  }

  /**
   * Which requirement to blame.
   *
   * The one that disqualifies the most attributable supply — that is the
   * constraint a staffing conversation would actually be about, and naming any
   * other one sends someone looking in the wrong place.
   */
  private diagnose(
    resourceType: string,
    requirements: PlanningRequirement[],
    priorityId: string | null,
    grain: AllocationGrain,
    qualifiedRemainingWU: number,
  ): QualificationShortfall | null {
    const candidates = this.states(resourceType, grain).filter(
      (s) =>
        s.unit.attributable &&
        (!s.unit.dedicatedToPriorityId || s.unit.dedicatedToPriorityId === priorityId),
    );

    let worst: QualificationShortfall | null = null;
    for (const requirement of requirements) {
      const failures = new Map<DisqualificationCode, number>();
      for (const state of candidates) {
        const verdict = unitSatisfies(state.unit, requirement, this.horizonEndDate);
        if (verdict.qualified || !verdict.code) continue;
        failures.set(verdict.code, (failures.get(verdict.code) ?? 0) + 1);
      }
      const total = [...failures.values()].reduce((a, b) => a + b, 0);
      if (total === 0) continue;

      // Report the most specific failure mode: "holds it, but below the level"
      // is a different conversation from "nobody holds it at all".
      const code =
        failures.get('below_level') != null
          ? 'below_level'
          : failures.get('credential_expires') != null
            ? 'credential_expires'
            : ([...failures.keys()][0] as DisqualificationCode);

      const candidate: QualificationShortfall = {
        requirement,
        code,
        failingUnitCount: failures.get(code) ?? total,
        qualifiedRemainingWU,
      };
      if (!worst || total > worst.failingUnitCount) worst = candidate;
    }

    // Every requirement is satisfiable in principle and the slice is simply
    // spent — the honest answer is plain capacity, not qualification.
    if (!worst && candidates.length > 0) return null;
    return worst;
  }

  /**
   * Books an item's demand against the units that could fund it.
   *
   * Called for Included and Deferred alike, mirroring the anonymous path where
   * a deferred item still raises running demand: an item that could not fit
   * must not leave its hours lying around for something further down the rank.
   * Drains largest-remaining first, with a stable id tiebreak so a run is
   * deterministic.
   */
  consume(args: {
    resourceType: string;
    amountWU: number;
    requirements: PlanningRequirement[];
    priorityId: string | null;
    grain: AllocationGrain;
  }): void {
    const { resourceType, amountWU, requirements, priorityId, grain } = args;
    if (amountWU <= 0) return;

    const eligible = this.eligible(resourceType, requirements, priorityId, grain).sort((a, b) => {
      const diff = this.remainingOf(b) - this.remainingOf(a);
      return diff !== 0 ? diff : a.unit.id.localeCompare(b.unit.id);
    });

    let outstanding = amountWU;
    for (const state of eligible) {
      if (outstanding <= 0) break;
      const take = Math.min(outstanding, this.remainingOf(state));
      state.consumed += take;
      outstanding -= take;
    }
    // Anything left over is unfunded demand. It is deliberately NOT charged to
    // ineligible units: overdrawing someone who could never have done the work
    // would corrupt the remainder every later item reads.
  }
}

// ---------------------------------------------------------------------------
// §3 — Saying it out loud
// ---------------------------------------------------------------------------

function requirementLabel(requirement: PlanningRequirement): string {
  switch (requirement.dimension) {
    case 'capability':
      return requirement.minLevel != null
        ? `${requirement.capabilityName ?? 'a capability'} at level ${requirement.minLevel} or above`
        : (requirement.capabilityName ?? 'a capability');
    case 'credential':
      return requirement.credentialName ?? 'a credential';
    case 'named_resource':
      return requirement.namedResourceName ?? 'a specific person';
  }
}

/**
 * The exclusion reason, at staffing-conversation resolution.
 *
 * Every dimension names the constraint AND what the roster actually holds, so
 * the sentence points at the next move rather than restating the verdict.
 */
export function explainQualificationGap(
  shortfall: QualificationShortfall,
  resourceType: string,
  poolRemainingWU: number,
): string {
  const { requirement, code, failingUnitCount, qualifiedRemainingWU } = shortfall;
  const label = requirementLabel(requirement);
  const people = `${failingUnitCount} ${failingUnitCount === 1 ? 'person' : 'people'}`;
  const slice = `Qualified pool ${qualifiedRemainingWU.toFixed(1)} WU against ${poolRemainingWU.toFixed(1)} WU of ${resourceType} left overall.`;

  switch (code) {
    case 'below_level':
      return `Requires ${label} — ${people} in ${resourceType} hold it below that level. ${slice}`;
    case 'lacks_capability':
      return `Requires ${label} — ${people} assigned to ${resourceType} do not hold it. ${slice}`;
    case 'lacks_credential':
      return `Requires ${label} — ${people} in ${resourceType} do not hold that credential. ${slice}`;
    case 'credential_expires':
      return `Requires ${label} — it expires inside this scenario's horizon for ${people}, so those hours cannot be counted on. ${slice}`;
    case 'not_the_named_resource':
      return `Only ${label} can do this, and their remaining hours are already committed above the line. ${slice}`;
    case 'unattributable':
      return `Requires ${label}, and the remaining ${resourceType} capacity is an anonymous pool row that no named resource stands behind. ${slice}`;
  }
}
